/**
 * UTM capture for the Quartel landing flow (Task 3).
 *
 * Two storage layers:
 *
 * 1. `localStorage["qb_utm_first"]` — only-set-once first-touch attribution.
 *    Survives across sessions until cleared by the user. Used by checkout
 *    to send the original campaign even after re-marketing.
 *
 * 2. `document.cookie["qb_utm"]` — always-overwrite last-touch. Domain
 *    `.quartelbot.com`, SameSite=Lax (so Google referrals carry the
 *    cookie), Max-Age 90d. Used by checkout to send the most-recent
 *    campaign.
 *
 * Both are populated on `/comprar` (and any landing the operator opts
 * in) when query-string UTMs are present. The checkout service merges
 * both into `payload.tracking.utm_*` (last) and a future
 * `payload.tracking.utm_first_*` (first).
 */

export type UtmKey =
  | "utm_source"
  | "utm_medium"
  | "utm_campaign"
  | "utm_term"
  | "utm_content"
  | "gclid"
  | "fbclid";

export const UTM_KEYS: readonly UtmKey[] = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
];

export type UtmSnapshot = Readonly<Partial<Record<UtmKey, string>>>;

const COOKIE_NAME = "qb_utm";
const FIRST_TOUCH_STORAGE_KEY = "qb_utm_first";
const MAX_AGE_SECONDS = 90 * 24 * 60 * 60; // 90 days

interface CaptureDeps {
  readonly hash?: { readonly search: string };
  readonly document?: { cookie: string };
  readonly storage?: Storage;
  readonly hostname?: string;
}

export function readUtmFromQuery(search: string): UtmSnapshot {
  const params = new URLSearchParams(
    search.startsWith("?") ? search : `?${search}`,
  );
  const out: Partial<Record<UtmKey, string>> = {};
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value && value.trim() !== "") out[key] = value.trim();
  }
  return out;
}

export function hasUtm(snapshot: UtmSnapshot): boolean {
  return UTM_KEYS.some((key) => Boolean(snapshot[key]));
}

export function writeLastTouchCookie(
  snapshot: UtmSnapshot,
  deps: CaptureDeps = {},
): void {
  if (!hasUtm(snapshot)) return;
  const docCookie = deps.document ?? document;
  const host = deps.hostname ?? window.location.hostname;
  const cookieDomain = cookieDomainFor(host);
  const value = encodeURIComponent(JSON.stringify(snapshot));
  const cookie = [
    `${COOKIE_NAME}=${value}`,
    `Max-Age=${MAX_AGE_SECONDS}`,
    "Path=/",
    "SameSite=Lax",
    cookieDomain ? `Domain=${cookieDomain}` : "",
  ]
    .filter(Boolean)
    .join("; ");
  docCookie.cookie = cookie;
}

export function writeFirstTouchStorage(
  snapshot: UtmSnapshot,
  storage: Storage = localStorage,
): void {
  if (!hasUtm(snapshot)) return;
  if (storage.getItem(FIRST_TOUCH_STORAGE_KEY)) return;
  storage.setItem(FIRST_TOUCH_STORAGE_KEY, JSON.stringify(snapshot));
}

export function readFirstTouchStorage(
  storage: Storage = localStorage,
): UtmSnapshot {
  const raw = storage.getItem(FIRST_TOUCH_STORAGE_KEY);
  if (!raw) return {};
  try {
    return JSON.parse(raw) as UtmSnapshot;
  } catch {
    return {};
  }
}

export function readLastTouchCookie(
  docCookie: string = document.cookie,
): UtmSnapshot {
  const match = docCookie.match(
    new RegExp(`(?:^|;\\s*)${COOKIE_NAME}=([^;]+)`),
  );
  if (!match) return {};
  try {
    return JSON.parse(decodeURIComponent(match[1] ?? "")) as UtmSnapshot;
  } catch {
    return {};
  }
}

export function captureUtm(deps: CaptureDeps = {}): UtmSnapshot {
  const search = (deps.hash ?? window.location).search;
  const snapshot = readUtmFromQuery(search);
  writeFirstTouchStorage(snapshot, deps.storage ?? localStorage);
  writeLastTouchCookie(snapshot, deps);
  return snapshot;
}

function cookieDomainFor(host: string): string | null {
  if (host.endsWith(".quartelbot.com") || host === "quartelbot.com") {
    return ".quartelbot.com";
  }
  // localhost / IPs / other hosts → host-only cookie (no Domain attr).
  return null;
}

export const __testing__ = {
  COOKIE_NAME,
  FIRST_TOUCH_STORAGE_KEY,
  MAX_AGE_SECONDS,
  cookieDomainFor,
};
